import Box from '@mui/material/Box';
import type {
  GridApi,
  GridReadyEvent,
  RowClickedEvent,
  StateUpdatedEvent
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useAtom, useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import LoadingIndicator from '@/components/LoadingIndicator';
import { DEFAULT_TIME_FILTER } from '@/components/TimeFilter/TimeFilter.constants';
import { DEFAULT_PAGE_SIZE } from '@/constants/common';
import useGridTheme from '@/hooks/useGridTheme';
import {
  CASES_INTERNAL_TABLE_LABEL,
  CASES_TABLE_DEFAULT_SORT_MODEL,
  CASES_TABLE_LABEL
} from '@/pages/Cases/Cases.constants';
import {
  casesFiltersAtom,
  casesGridApiAtom,
  casesTimeFilterAtom,
  getCaseServerSideDatasource,
  internalCasesFiltersAtom,
  internalCasesGridApiAtom,
  internalCasesTimeFilterAtom
} from '@/pages/Cases/Cases.store';
import type { CaseList } from '@/pages/Cases/Cases.types';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import {
  gridGetStoredState,
  gridSetPage,
  gridSetStoreState
} from '@/utils/Grid.utils';
import { loadingCellRenderer } from '@/utils/GridRenderer.utils';

import { CASE_GRID_OPTIONS } from './CaseTable.constants';
import {
  getCaseColumnDefs,
  getInternalCaseColumnDefs
} from './CaseTable.utils';

const CaseTable = () => {
  const navigate = useNavigate();
  const gridTheme = useGridTheme();
  const [, setCasesGridApi] = useAtom(casesGridApiAtom);
  const [, setInternalCasesGridApi] = useAtom(internalCasesGridApiAtom);
  const caseFilters = useAtomValue(casesFiltersAtom);
  const caseTimeFilter = useAtomValue(casesTimeFilterAtom);
  const caseInternalFilters = useAtomValue(internalCasesFiltersAtom);
  const caseInternalTimeFilter = useAtomValue(internalCasesTimeFilterAtom);
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);
  const { pathname } = useLocation();
  const isInternalCasePage = pathname.includes('/cases-internal');

  const [isFetching, setIsFetching] = useState(false);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);

  const casesTableLabel = useMemo(
    () => (isInternalCasePage ? CASES_INTERNAL_TABLE_LABEL : CASES_TABLE_LABEL),
    [isInternalCasePage]
  );
  const casesTableStateKey = `${casesTableLabel}State`;

  const { initialState, pageSize } = useMemo(() => {
    const storedState = gridGetStoredState(
      casesTableLabel,
      CASES_TABLE_DEFAULT_SORT_MODEL
    );

    return {
      initialState: storedState,
      pageSize: storedState.pagination?.pageSize ?? DEFAULT_PAGE_SIZE
    };
  }, [casesTableLabel, isInternalCasePage]);

  const cacheBlockSize = pageSize * 5;
  const pageTimeFilter = useMemo(
    () => (isInternalCasePage ? caseInternalTimeFilter : caseTimeFilter),
    [isInternalCasePage, caseInternalTimeFilter, caseTimeFilter]
  );
  const pageFilter = useMemo(
    () => (isInternalCasePage ? caseInternalFilters : caseFilters),
    [isInternalCasePage, caseInternalFilters, caseFilters]
  );

  const pageFilterKey = JSON.stringify(pageFilter);
  const pageTimeFilterKey = JSON.stringify(pageTimeFilter);
  const memoizedDatasource = useMemo(
    () =>
      getCaseServerSideDatasource(
        selectedOrganization.label as string,
        pageFilter,
        pageTimeFilter || DEFAULT_TIME_FILTER,
        setIsFetching
      ),
    // Serialize objects so useMemo compares by value, not reference.
    // filters and time are arrays/objects that may get new
    // references on every render even when the content hasn't changed.

    [
      selectedOrganization.label,
      pageFilterKey,
      pageTimeFilterKey,
      setIsFetching
    ]
  );

  useEffect(() => {
    if (gridApi && !gridApi.isDestroyed()) {
      gridApi.setGridOption('serverSideDatasource', memoizedDatasource);
    }
  }, [memoizedDatasource, gridApi]);

  const handleGridReady = useCallback(
    (params: GridReadyEvent) => {
      setGridApi(params.api);

      if (isInternalCasePage) {
        setInternalCasesGridApi(params.api);
        return;
      }

      setCasesGridApi(params.api);
    },
    [isInternalCasePage, setGridApi, setInternalCasesGridApi]
  );

  useEffect(() => {
    if (!gridApi || gridApi.isDestroyed()) return;

    if (isInternalCasePage) {
      setInternalCasesGridApi(gridApi);
      return;
    }

    setCasesGridApi(gridApi);
  }, [gridApi, isInternalCasePage, setCasesGridApi, setInternalCasesGridApi]);

  const handleRowClicked = useCallback(
    (event: RowClickedEvent<CaseList>) => {
      const target = event.event?.target as HTMLElement | undefined;
      const isTicketIdCell = target?.closest('[col-id="scope_ticket_id"]');

      if (isTicketIdCell) {
        // Let the Ticket ID cell's own anchor handle navigation to /ticket
        return;
      }

      if (event.data?.id) {
        const mouseEvent = event.event as MouseEvent | undefined;
        if (mouseEvent?.ctrlKey || mouseEvent?.metaKey) {
          // Open in new tab if Ctrl, or Command key is pressed
          if (isInternalCasePage) {
            window.open(`/case-internal/${event.data.soc_id}`, '_blank');
            return;
          }
          window.open(`/case/${event.data.soc_id}`, '_blank');
          return;
        }

        navigate(
          `/case${isInternalCasePage ? '-internal' : ''}/${event.data.soc_id}`
        );
      }
    },
    [navigate, isInternalCasePage]
  );

  const handleStateUpdate = useCallback(
    (event: StateUpdatedEvent) => gridSetStoreState(event, casesTableLabel),
    [casesTableLabel]
  );

  const columnDefs = useMemo(
    () =>
      pathname === '/cases'
        ? getCaseColumnDefs(selectedOrganization)
        : getInternalCaseColumnDefs(selectedOrganization),
    [selectedOrganization, pathname]
  );

  const gridOptions = useMemo(() => {
    return {
      ...CASE_GRID_OPTIONS,
      initialState,
      onGridReady: handleGridReady
    };
  }, [handleGridReady]);

  useEffect(() => {
    if (gridApi && !gridApi.isDestroyed()) {
      gridApi.paginationGoToFirstPage();
    }
  }, [selectedOrganization, gridApi]);

  return (
    <Box
      className="ag-theme-alpine rounded-4px"
      sx={{ border: 'none !important' }}
    >
      <LoadingIndicator isLoading={isFetching} noBackground />
      <AgGridReact
        columnDefs={columnDefs}
        gridOptions={gridOptions}
        rowModelType="serverSide"
        theme={gridTheme}
        onStateUpdated={handleStateUpdate}
        onRowClicked={handleRowClicked}
        cacheBlockSize={cacheBlockSize}
        onFirstDataRendered={(params) =>
          gridSetPage(params.api, casesTableStateKey)
        }
        tooltipShowDelay={0}
        loading={false}
        loadingCellRenderer={loadingCellRenderer}
      />
    </Box>
  );
};

export default CaseTable;
